import { useMutation } from '@apollo/client';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { Gender } from '../../graphql/graphql';
import { SET_USER_GENDER } from '../../graphql/mutations-gql';
import { USER } from '../../graphql/queries-gql';
import { useDismissableSnackbar } from '../../hooks/useDismissableSnackbar';

const OPTIONS = [
  { value: Gender.Man, label: 'Man' },
  { value: Gender.Woman, label: 'Woman' },
  { value: Gender.NonBinary, label: 'Non-binary' },
];

export default function ChangeUserGender({ userId, gender }: { userId: string; gender: Gender }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Gender | ''>('');
  const [error, setError] = useState('');
  const { showSnackbar } = useDismissableSnackbar();
  const [saveGender, { loading }] = useMutation<
    { setUserGender: string | null },
    { userId: string; gender: Gender }
  >(SET_USER_GENDER, {
    refetchQueries: [{ query: USER, variables: { _id: userId } }],
    awaitRefetchQueries: true,
  });

  const save = async () => {
    if (!selected || selected === gender || loading) return;
    setError('');
    try {
      await saveGender({ variables: { userId, gender: selected } });
      setOpen(false);
      showSnackbar('Gender updated', 'success');
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Could not update gender. Please try again.');
    }
  };

  return (
    <>
      <Button size="small" onClick={() => {
        setSelected(OPTIONS.find(option => option.value === gender)?.value ?? '');
        setError('');
        setOpen(true);
      }}>
        Change gender
      </Button>
      <Dialog open={open} onClose={() => { if (!loading) setOpen(false); }} fullWidth maxWidth="xs" aria-labelledby="change-user-gender-title">
        <DialogTitle id="change-user-gender-title">Change gender</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            margin="dense"
            label="Gender"
            value={selected}
            disabled={loading}
            onChange={event => {
              setSelected(OPTIONS.find(option => option.value === event.target.value)?.value ?? '');
              setError('');
            }}
          >
            {OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={loading || !selected || selected === gender} onClick={save}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
